/* screens/kitchen/editors/TTKEditor.jsx — редактор ТТК (drawer type='ttk').
   Самая сложная форма продукта: 7 разделов ГОСТ, inline-состав с замерами,
   пикеры сырья/п-ф, аллергены, derived-метрики (нередактируемы), экспорт DOCX.
   Вкладки: Карта / Использование / История. Map = AnchorNav(Р1–Р7) + разделы.
   Данные — боевой getTTKDetail (lib/api); derived — lib/ttkCompute (живой пересчёт при правках).
   ХЕНДОФФ → frontend/src/pages/kitchen/TTKEditor.jsx. */

import React from 'react';
import { useChat } from '../../../app/providers/ChatProvider.jsx';
import { useConfirm } from '../../../app/providers/ConfirmProvider.jsx';
import { useToast } from '../../../app/providers/ToastProvider.jsx';
import { useUnsaved } from '../../../app/providers/UnsavedChangesProvider.jsx';
import { AllergenAutoHint, AllergenChips } from '../../../components/kitchen/AllergenControls.jsx';
import { KitchenPicker, MultiGramsStep } from '../../../components/kitchen/IngredientPickers.jsx';
import { IngredientsTable } from '../../../components/kitchen/IngredientsTable.jsx';
import { EntityEditor, EntityTitleInput } from '../../../components/patterns/EntityEditor.jsx';
import { EmptyState, QueryBoundary } from '../../../components/patterns/States.jsx';
import { CostSummaryBlock, KbjuTable, TTKUsedInBlock, YieldHint } from '../../../components/ttk/TTKViewBlocks.jsx';
import { KindBadge, StatusBadge } from '../../../components/ui/Badge.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { IconButton } from '../../../components/ui/Buttons.jsx';
import { Field, NumberInput, Textarea } from '../../../components/ui/Field.jsx';
import { Icon } from '../../../components/ui/Icon.jsx';
import { Modal } from '../../../components/ui/Modal.jsx';
import { VersionsBlock } from '../../../components/versioning/VersionsBlock.jsx';
import { hooks } from '../../../hooks/index.jsx';
import {
  getTTKDetail, createTTKReal, saveTTKDraft, deleteTTKReal,
  syncTTKRows, ttkExportUrl, pickerFetch, downloadFile,
} from '../../../lib/api/index.js';
import { serverDerived, blankTTKDetail } from '../../../lib/adapters/ttk.js';
import { fmt } from '../../../lib/format.js';
import { RR } from '../../../lib/router.jsx';
import { ttkCompute as TC } from '../../../lib/ttkCompute.js'; // только boundCount/unboundCount (чистые)

  const { useState, useEffect, useMemo } = React;
  const { useFakeQuery, useDrawerStack } = hooks;
  const { useSearchParams } = RR;

  let _rid = 1;
  const tagRows = (rows) => (rows || []).map((r) => ({ _rid: r._rid || 'r' + (_rid++), ...r }));

  let _sid = 1;
  // шаги техпроцесса: из detail.technology_steps или из строки detail.technology (split по \n)
  function parseTechSteps(detail) {
    if (!detail) return [];
    if (Array.isArray(detail.technology_steps)) return detail.technology_steps;
    const txt = (detail.technology || '').trim();
    if (!txt) return [];
    return txt.split(/\n+/).map((t) => t.trim()).filter(Boolean).map((t, i) => ({ id: 'st-init-' + i, text: t }));
  }

  function readPrefill() {
    let kind = 'dish', fields = null, rows = null;
    try {
      const a = sessionStorage.getItem('kitchen:new-ttk');
      if (a) { const o = JSON.parse(a); if (o.kind) kind = o.kind; sessionStorage.removeItem('kitchen:new-ttk'); }
      const b = sessionStorage.getItem('kitchen:ttk-prefill');
      if (b) { const o = JSON.parse(b); fields = o.fields || null; rows = o.rows || null; if (o.kind) kind = o.kind; sessionStorage.removeItem('kitchen:ttk-prefill'); }
    } catch (e) {}
    return { kind, fields, rows };
  }

  function TTKEditor({ id, onClose }) {
    const isNew = id === 'new' || String(id).startsWith('new-');
    const { push, stack } = useDrawerStack();
    const [params] = useSearchParams();
    const chat = useChat ? useChat() : null;
    const toast = useToast();
    const confirm = useConfirm();
    const unsaved = useUnsaved ? useUnsaved() : null;
    const clearGuard = () => unsaved && unsaved.register && unsaved.register('ttk-' + id, false);

    const query = useFakeQuery('ttk-' + id, () => (isNew ? Promise.resolve(null) : getTTKDetail(id)), [id]);

    const [detail, setDetail] = useState(null);
    const [dirty, setDirty] = useState(false);
    // ?v=<n> или ?etab=history → открыть вкладку «История» при входе (deep-link версий, S13 §2.4).
    // &v скоупится к ВЕРХНЕМУ сегменту ?edit (IA §4.1) — читаем v только если этот редактор верхний.
    const isTop = !!stack.length && stack[stack.length - 1].type === 'ttk' && String(stack[stack.length - 1].id) === String(id);
    const wantHistory = !isNew && ((isTop && !!params.get('v')) || params.get('etab') === 'history');
    const [tab, setTab] = useState(wantHistory ? 'history' : 'recipe');
    const [busy, setBusy] = useState(false);
    const [titleErr, setTitleErr] = useState(null);
    const scale = '1'; // масштаб нормы убран из UI — состав показываем в норме ×1
    const [addOpen, setAddOpen] = useState(false);  // модалка выбора типа состава
    const [picker, setPicker] = useState(null);     // {kind, multi}
    const [gramsFor, setGramsFor] = useState(null);  // entities[] awaiting grams

    // инициализация detail (new → blank+prefill; existing → из запроса)
    useEffect(() => {
      if (isNew) {
        const { kind, fields, rows } = readPrefill();
        const base = blankTTKDetail(kind);
        const d = { ...base, ...(fields || {}), ingredient_rows: tagRows(rows || []) };
        setDetail(d);
      } else if (query.data) {
        setDetail({ ...query.data, ingredient_rows: tagRows(query.data.ingredient_rows) });
      }
      // eslint-disable-next-line
    }, [query.data, id]);

    // derived — с сервера (cost/allergens), потери/fc%/КБЖУ-на-100г из величин. НЕ TC.recompute (тот по мокам).
    const derived = useMemo(() => (detail ? serverDerived(detail) : null), [detail]);
    const rows = derived ? derived.ingredient_rows : [];
    const boundN = TC.boundCount(detail ? detail.ingredient_rows : []);
    const totalN = detail ? detail.ingredient_rows.length : 0;
    const unboundN = TC.unboundCount(detail ? detail.ingredient_rows : []);

    const markDirty = () => setDirty(true);
    const patch = (p) => { setDetail((d) => ({ ...d, ...p })); markDirty(); };
    const patchRows = (next) => { setDetail((d) => ({ ...d, ingredient_rows: next })); markDirty(); };

    const onRowChange = (rid, p) => patchRows(detail.ingredient_rows.map((r) => (r._rid === rid ? { ...r, ...p } : r)));
    const onRebind = (rid, entity) => patchRows(detail.ingredient_rows.map((r) => (r._rid === rid
      ? { ...r, target_kind: entity ? entity.kind : null, target_id: entity ? entity.id : null, target_label: entity ? entity.label : null }
      : r)));
    const onDeleteRow = async (rid) => {
      const ok = await confirm({ title: 'Удалить строку состава?', body: 'Строка будет удалена из рецептуры. Действие можно отменить, не сохраняя ТТК.', confirmLabel: 'Удалить', tone: 'danger' });
      if (ok) patchRows(detail.ingredient_rows.filter((r) => r._rid !== rid));
    };
    const onOpenTarget = (r) => {
      if (r.target_kind === 'card') push('card', r.target_id);
      else if (r.target_kind === 'ttk') push('ttk', r.target_id);
    };

    const addRows = (newRows) => { patchRows([...detail.ingredient_rows, ...tagRows(newRows)]); setGramsFor(null); setPicker(null); };

    // ── Защита от циклов вложенных п/ф (P2-28) ──
    // Недоступны для привязки: сама ТТК + её предки (parents + цепочка открытых ТТК-редакторов в стеке).
    // Канон: п/ф МОЖЕТ содержать п/ф, но НЕ себя и НЕ предка (code='cycle', MAX_RECIPE_DEPTH=10).
    const MAX_RECIPE_DEPTH = 10;
    const pfExcludeIds = useMemo(() => {
      const ids = new Set();
      if (id != null && !isNew) ids.add(String(id));
      (detail && detail.parents ? detail.parents : []).forEach((p) => p && p.ttk_id != null && ids.add(String(p.ttk_id)));
      (stack || []).forEach((e) => { if (e && e.type === 'ttk' && String(e.id) !== String(id)) ids.add(String(e.id)); });
      return [...ids];
    }, [id, isNew, detail && detail.parents, stack]);
    // глубина текущей ветки = открытые ТТК-редакторы в стеке (грубая оценка для прототипа)
    const ttkDepth = (stack || []).filter((e) => e && e.type === 'ttk').length;
    const [bindErr, setBindErr] = useState(null); // inline-ошибка привязки п/ф: {code:'cycle'|'depth', label}

    // строки состава-полуфабрикаты без привязки (target_kind='ttk', target_id=null) →
    // кандидаты на spawn дочернего чата проработки п/ф (S13 §2.2)
    const spawnableRows = (detail ? detail.ingredient_rows : []).filter(
      (r) => r.target_kind === 'ttk' && (r.target_id == null));
    const spawnPf = (row) => {
      if (!chat || !chat.spawnChat) { toast.show('Чат недоступен', 'muted'); return; }
      chat.spawnChat({
        skill: 'ttk-generate', spawnFor: id, parentTitle: detail.title,
        title: 'П/ф «' + (row.name_raw || 'полуфабрикат') + '»',
        prefill: 'Разработай полуфабрикат «' + (row.name_raw || '') + '» для ТТК «' + detail.title + '».',
      });
    };

    // выбор типа состава из модалки «Добавить»
    const onAddPick = (key) => {
      setAddOpen(false);
      setBindErr(null);
      if (key === 'card') setPicker({ kind: 'card', multi: true });
      else if (key === 'semiproduct') {
        // защита по глубине вложенности до открытия пикера (MAX_RECIPE_DEPTH)
        if (ttkDepth >= MAX_RECIPE_DEPTH) {
          setBindErr({ code: 'depth', label: 'Достигнута максимальная глубина вложенности полуфабрикатов (' + MAX_RECIPE_DEPTH + '). Дальше вкладывать нельзя.' });
          return;
        }
        setPicker({ kind: 'semiproduct', multi: true });
      }
    };

    // ── Технологический процесс: конструктор шагов (Р4) ──
    const techSteps = parseTechSteps(detail);
    const setTechSteps = (next) => patch({ technology_steps: next, technology: next.map((s) => s.text).join('\n') });
    const addStep = () => setTechSteps([...techSteps, { id: 'st' + (_sid++), text: '' }]);
    const updateStep = (sid, text) => setTechSteps(techSteps.map((s) => (s.id === sid ? { ...s, text } : s)));
    const moveStep = (sid, dir) => {
      const arr = [...techSteps]; const i = arr.findIndex((s) => s.id === sid); const j = i + dir;
      if (j < 0 || j >= arr.length) return; const t = arr[i]; arr[i] = arr[j]; arr[j] = t; setTechSteps(arr);
    };
    const deleteStep = (sid) => setTechSteps(techSteps.filter((s) => s.id !== sid));

    const save = async () => {
      if (!detail.title.trim()) { setTitleErr('Укажите название ТТК'); setTab('recipe'); return; }
      if (detail.kind === 'semiproduct' && !(detail.output_g > 0)) { toast.show('Для полуфабриката укажите выход (г)', 'danger'); setTab('recipe'); return; }
      setBusy(true); setTitleErr(null);
      try {
        if (isNew) {
          await createTTKReal(detail);
        } else {
          await saveTTKDraft(id, detail);
          await syncTTKRows(id, (query.data && query.data.ingredient_rows) || [], detail.ingredient_rows);
        }
        window.dispatchEvent(new CustomEvent('kitchen:ttks-changed'));
        toast.show(isNew ? 'ТТК создана' : 'Сохранено. Версия зафиксирована.', 'success');
        setDirty(false); clearGuard();
        onClose && onClose();
      } catch (e) {
        if (e.code === 'duplicate_title' || e.status === 409) { setTitleErr('ТТК с таким названием уже существует'); setTab('recipe'); }
        else if (e.code === 'cycle') { toast.show('Цикл вложенных полуфабрикатов — проверьте состав', 'danger', 4000); }
        else toast.show(e.detail || 'Не удалось сохранить', 'danger');
        setBusy(false);
      }
    };

    const del = async () => {
      const ok = await confirm({ title: 'Удалить ТТК?', body: 'ТТК «' + detail.title + '» будет удалена. Это действие необратимо.', confirmLabel: 'Удалить ТТК', tone: 'danger' });
      if (!ok) return;
      setBusy(true);
      try {
        await deleteTTKReal(id);
        window.dispatchEvent(new CustomEvent('kitchen:ttks-changed'));
        toast.show('ТТК удалена', 'success');
        setDirty(false); clearGuard();
        onClose && onClose();
      } catch (e) {
        if (e.code === 'ttk_in_use') toast.show(e.detail, 'danger', 5000);
        else toast.show('Не удалось удалить', 'danger');
        setBusy(false);
      }
    };

    // Скачивание blob'ом с X-Workspace-Id: window.open не шлёт заголовок,
    // и ТТК business-пространства ловила 404 «битым» файлом.
    const exportDocx = async () => {
      if (isNew) return;
      const r = await downloadFile(ttkExportUrl(id));
      if (!r.ok) toast.show((r.data && r.data.detail) || 'Не удалось скачать DOCX', 'danger');
    };

    // ── состояния загрузки/ошибки ──
    if (!isNew && (query.loading || (!detail && !query.error))) {
      return <div className="ttk-editor"><div className="ttk-skel">{[0,1,2,3].map((i) => <div key={i} className="skeleton ttk-skel__block" />)}</div></div>;
    }
    if (!isNew && query.error) {
      return <div className="ttk-editor"><QueryBoundary error={query.error} onRetry={query.reload}>{null}</QueryBoundary></div>;
    }
    if (!detail) return null;


    const titleField = (
      <Field error={titleErr} className="ttk-title-field">
        <EntityTitleInput value={detail.title} placeholder="Название ТТК" invalid={titleErr} ariaLabel="Название ТТК"
          onChange={(e) => { patch({ title: e.target.value }); if (titleErr) setTitleErr(null); }} />
      </Field>
    );

    // «Редактировать в чате»: новый чат с привязкой ЭТОЙ ТТК (metadata.edit_target)
    // — модель правит её через propose_ttk_edit, изменения приходят канвасом на одобрение.
    const editInChat = () => {
      if (!chat || !chat.spawnEditChat) { toast.show('Чат недоступен', 'muted'); return; }
      chat.spawnEditChat({ kind: 'ttk', id, title: detail.title });
    };

    const headActions = (
      <>
        <IconButton icon="sparkles" variant="ghost" size="sm" tooltip="Редактировать в чате" onClick={editInChat} disabled={isNew} />
        <IconButton icon="download" variant="ghost" size="sm" tooltip="Скачать DOCX" onClick={exportDocx} disabled={isNew} />
      </>
    );

    const footer = (
      <>
        {!isNew && <Button variant="ghost" iconLeft="trash" onClick={del} disabled={busy} className="ttk-foot__del">Удалить</Button>}
        <div className="grow" />
        <Button variant="ghost" onClick={() => onClose && onClose()} disabled={busy}>Отмена</Button>
        <Button variant="primary" onClick={save} loading={busy} disabled={!dirty && !isNew}>{isNew ? 'Создать ТТК' : 'Сохранить'}</Button>
      </>
    );

    const tabs = [
      { value: 'recipe', label: 'Рецептура' },
      { value: 'map', label: 'Требования' },
      { value: 'usage', label: 'Использование', disabled: isNew },
      { value: 'history', label: 'История', disabled: isNew },
    ].filter((t) => !(t.disabled));

    // ── Рецептура (Р3) + Технологический процесс (Р4) — первая вкладка ──
    const recipeR3 = (
          <Section id="ttk-r3" num={3} title="Рецептура">
            <div className="ttk-r3-fields">
              <Field label={<span className="ttk-yield-label">Выход порции <YieldHint /></span>}>
                <NumberInput value={detail.output_g ?? ''} unit="г" step={10} min={0} onChange={(v) => patch({ output_g: v === '' ? null : Number(v) })} />
              </Field>
              {detail.kind === 'dish' && (
                <Field label="Цена продажи">
                  <NumberInput value={detail.selling_price_rub ?? ''} unit="₽" step={10} min={0} onChange={(v) => patch({ selling_price_rub: v === '' ? null : Number(v) })} />
                </Field>
              )}
            </div>

            <div className="ttk-composition">
            {/* Состав — заголовок */}
            <div className="ttk-composition__head">
              <h4 className="ttk-sub-title t-caption">Состав{totalN > 0 ? ' · ' + totalN : ''}</h4>
            </div>

            {/* warning непривязанных: состав собирается только из реестра, поэтому
                ремонт непривязанной (легаси) строки — удалить и добавить из реестра. */}
            {unboundN > 0 && (
              <div className="ttk-unbound-banner">
                <Icon name="alert-triangle" size={18} />
                <span className="grow">{unboundN} {fmt.plural(unboundN, 'строка не привязана', 'строки не привязаны', 'строк не привязаны')} к карточкам — себестоимость и аллергены неполные. Удалите такие строки и добавьте ингредиенты из реестра.</span>
              </div>
            )}

            {/* inline-ошибка привязки п/ф: цикл (сама ТТК/предок) или превышение глубины (P2-28) */}
            {bindErr && (
              <div className="ttk-bind-error" role="alert">
                <Icon name="alert-circle" size={18} />
                <span className="grow">{bindErr.label}</span>
                <IconButton icon="close" size="sm" variant="ghost" tooltip="Скрыть" onClick={() => setBindErr(null)} />
              </div>
            )}

            {totalN === 0 ? (
              <EmptyState serif={false} icon="utensils" title="Состав пуст"
                body="Добавьте ингредиенты из реестра сырья или полуфабрикаты со своей ТТК."
                action={<Button variant="primary" size="sm" iconLeft="plus" onClick={() => setAddOpen(true)}>Добавить ингредиент</Button>} />
            ) : (
              <div className="ttk-composition__list">
                <IngredientsTable rows={rows} scale={scale}
                  onRowChange={onRowChange} onDelete={onDeleteRow} onRebind={onRebind} onOpenTarget={onOpenTarget} />
                <button type="button" className="ttk-add-row" onClick={() => setAddOpen(true)}>
                  <Icon name="plus" size={16} />
                  <span>Добавить ингредиент</span>
                </button>
              </div>
            )}

            {/* spawn п/ф — для строк-полуфабрикатов без привязки (S13 §2.2) */}
            {spawnableRows.length > 0 && (
              <div className="ttk-spawn">
                <div className="ttk-spawn__head">
                  <Icon name="sparkles" size={15} />
                  <span>Полуфабрикаты без своей ТТК — разработайте их в отдельном чате:</span>
                </div>
                <ul className="ttk-spawn__list">
                  {spawnableRows.map((r) => (
                    <li key={r._rid} className="ttk-spawn__row">
                      <span className="ttk-spawn__name truncate">{r.name_raw || 'Полуфабрикат'}</span>
                      <Button variant="secondary" size="sm" iconLeft="sparkles" onClick={() => spawnPf(r)} className="ttk-spawn__btn">
                        Разработать п/ф
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            </div>
          </Section>
    );
    const recipeR4 = (
          <Section id="ttk-r4" num={4} title="Технологический процесс">
            <TechStepsEditor steps={techSteps} onAdd={addStep} onChange={updateStep} onMove={moveStep} onDelete={deleteStep} />
          </Section>
    );
    const recipeTab = (
      <div className="ttk-map">
        <div className="ttk-map__body">
          {recipeR3}
          {recipeR4}
        </div>
      </div>
    );

    // ── Требования ГОСТ (Р1, Р2, Р5, Р6, Р7) ──
    const mapTab = (
      <div className="ttk-map">
        <div className="ttk-map__body">
          {/* Р1 */}
          <Section id="ttk-r1" num={1} title="Область применения">
            <Textarea value={detail.application_scope} minRows={2} placeholder="На какое блюдо/изделие распространяется ТТК…"
              onChange={(e) => patch({ application_scope: e.target.value })} />
          </Section>
          {/* Р2 */}
          <Section id="ttk-r2" num={2} title="Требования к сырью">
            <Textarea value={detail.raw_material_requirements} minRows={2} placeholder="Требования к качеству и безопасности сырья…"
              onChange={(e) => patch({ raw_material_requirements: e.target.value })} />
          </Section>
          {/* Р5 */}
          <Section id="ttk-r5" num={5} title="Оформление, подача и хранение">
            <Field label="Оформление и подача">
              <Textarea value={detail.plating_and_service} minRows={2} onChange={(e) => patch({ plating_and_service: e.target.value })} />
            </Field>
            <Field label="Условия и сроки хранения">
              <Textarea value={detail.storage_conditions} minRows={2} onChange={(e) => patch({ storage_conditions: e.target.value })} />
            </Field>
          </Section>
          {/* Р6 */}
          <Section id="ttk-r6" num={6} title="Требования к качеству">
            <div className="ttk-quality-grid">
              <Field label="Внешний вид"><Textarea value={detail.appearance} minRows={1} onChange={(e) => patch({ appearance: e.target.value })} /></Field>
              <Field label="Консистенция"><Textarea value={detail.texture} minRows={1} onChange={(e) => patch({ texture: e.target.value })} /></Field>
              <Field label="Вкус"><Textarea value={detail.taste} minRows={1} onChange={(e) => patch({ taste: e.target.value })} /></Field>
              <Field label="Запах"><Textarea value={detail.smell} minRows={1} onChange={(e) => patch({ smell: e.target.value })} /></Field>
              <Field label="Микробиология" className="ttk-quality-grid__wide"><Textarea value={detail.microbiology} minRows={1} onChange={(e) => patch({ microbiology: e.target.value })} /></Field>
            </div>
            <div className="ttk-allergens">
              <h4 className="ttk-sub-title t-caption">Аллергены (ТР ТС 022/2011)</h4>
              <AllergenChips value={detail.allergens} onChange={(v) => patch({ allergens: v })} />
              <AllergenAutoHint items={derived.allergens_auto} manual={detail.allergens} />
            </div>
          </Section>
          {/* Р7 */}
          <Section id="ttk-r7" num={7} title="Пищевая ценность и себестоимость">
            <div className="ttk-r7">
              <div className="ttk-r7__kbju">
                <div className="kbju-edit__head">
                  <h4 className="ttk-sub-title t-caption">КБЖУ</h4>
                </div>
                <KbjuTable per_portion={detail.kbju_per_portion} per_100g={derived.kbju_per_100g} className="derived-zone" />
                <p className="kbju-edit__note t-caption text-mute">КБЖУ считается автоматически по составу и выходу. Значения нередактируемы — это derived-величины.</p>
              </div>
              <div className="ttk-r7__cost">
                <h4 className="ttk-sub-title t-caption">Себестоимость</h4>
                <CostSummaryBlock cost_per_portion_rub={derived.cost_per_portion_rub} cost_per_100g_rub={derived.cost_per_100g_rub}
                  output_g={detail.output_g} selling_price_rub={detail.selling_price_rub} cost_status={derived.cost_status} fc_pct={derived.fc_pct} kind={detail.kind} />
              </div>
            </div>
          </Section>
        </div>
      </div>
    );

    const usageTab = (
      <div className="ttk-usage">
        <TTKUsedInBlock parents={detail.parents} menus={detail.menus}
          onOpenTtk={(tid) => push('ttk', tid)} onOpenMenu={(mid) => push('menu', mid)} />
      </div>
    );

    const historyTab = (
      <div className="ttk-history">
        <VersionsBlock kind="ttk" id={id} currentSnapshot={{
          title: detail.title, output_g: detail.output_g, storage_conditions: detail.storage_conditions,
          allergens: detail.allergens, kbju_per_portion: detail.kbju_per_portion,
          ingredient_rows: (detail.ingredient_rows || []).map((r) => ({ name_raw: r.name_raw, target_label: r.target_label, amount_brutto_g: r.amount_brutto_g, amount_netto_g: r.amount_netto_g })),
        }} />
      </div>
    );

    return (
      <EntityEditor
        dirtyKey={'ttk-' + id} dirty={dirty}
        titleField={titleField}
        kindBadge={<KindBadge kind={detail.kind === 'semiproduct' ? 'semi' : 'dish'} size="sm" />}
        status={!isNew && <StatusBadge status={detail.status} size="sm" />}
        headActions={headActions}
        tabs={tabs} activeTab={tab} onTab={setTab}
        footer={footer}>
        {tab === 'recipe' && recipeTab}
        {tab === 'map' && mapTab}
        {tab === 'usage' && usageTab}
        {tab === 'history' && historyTab}

        {picker && (
          <KitchenPicker open kind={picker.kind} multi={picker.multi}
            fetcher={(s) => pickerFetch(picker.kind, s)}
            excludeIds={picker.kind === 'semiproduct' ? pfExcludeIds : undefined}
            onClose={() => setPicker(null)}
            onConfirm={(entities) => {
              // defensive: отсеять предков/саму ТТК, если как-то просочились (цикл)
              const ex = new Set(pfExcludeIds.map(String));
              const safe = (entities || []).filter((e) => !ex.has(String(e.id)));
              if (picker.kind === 'semiproduct' && safe.length < (entities || []).length) {
                setBindErr({ code: 'cycle', label: 'Нельзя вложить эту ТТК: она содержит текущую (или является её предком) — получился бы цикл.' });
              }
              setPicker(null);
              if (safe.length) setGramsFor(safe);
            }} />
        )}
        {addOpen && <AddCompositionModal open onClose={() => setAddOpen(false)} onPick={onAddPick} />}
        {gramsFor && (
          <MultiGramsStep open entities={gramsFor}
            onBack={() => { setPicker({ kind: gramsFor[0] && gramsFor[0].kind === 'ttk' ? 'semiproduct' : 'card', multi: true }); setGramsFor(null); }}
            onClose={() => setGramsFor(null)}
            onConfirm={addRows} />
        )}
      </EntityEditor>
    );
  }

  function Section({ id, num, title, children }) {
    return (
      <section id={id} className="ttk-section">
        <header className="ttk-section__head">
          <h3 className="ttk-section__title t-h4">{title}</h3>
        </header>
        <div className="ttk-section__body">{children}</div>
      </section>
    );
  }

  /* AddCompositionModal — выбор типа добавляемого в состав с кратким описанием отличий. */
  const ADD_OPTIONS = [
    { key: 'card', icon: 'tag', title: 'Ингредиент из реестра', desc: 'Закупаемое сырьё из реестра. Берёт цену, КБЖУ и аллергены из карточки — себестоимость считается автоматически.' },
    { key: 'semiproduct', icon: 'box', title: 'Полуфабрикат из реестра', desc: 'Собственная заготовка со своей ТТК: соус, бульон, тесто. Входит в блюдо строкой со своей себестоимостью.' },
  ];
  function AddCompositionModal({ open, onClose, onPick }) {
    return (
      <Modal open={open} onClose={onClose} size="sm" title="Что добавить в состав?"
        footer={<Button variant="ghost" onClick={onClose}>Отмена</Button>}>
        <ul className="add-choice">
          {ADD_OPTIONS.map((o) => (
            <li key={o.key}>
              <button type="button" className="add-choice__item" onClick={() => onPick(o.key)}>
                <span className="add-choice__top">
                  <span className="add-choice__icon"><Icon name={o.icon} size={20} /></span>
                  <span className="add-choice__title">{o.title}</span>
                  <Icon name="chevron-right" size={16} className="add-choice__chev" />
                </span>
                <span className="add-choice__desc">{o.desc}</span>
              </button>
            </li>
          ))}
        </ul>
      </Modal>
    );
  }

  /* TechStepsEditor — конструктор техпроцесса: нумерованные шаги, добавление, перемещение ↑/↓, удаление. */
  function TechStepsEditor({ steps, onAdd, onChange, onMove, onDelete }) {
    return (
      <div className="tech-steps">
        {steps.length === 0 ? (
          <p className="tech-steps__empty t-body-sm text-mute">Опишите приготовление по шагам — так процесс проще читать на кухне.</p>
        ) : (
          <ol className="tech-steps__list">
            {steps.map((s, i) => (
              <li key={s.id} className="tech-step">
                <span className="tech-step__num num">{i + 1}</span>
                <Textarea value={s.text} minRows={1} placeholder={'Шаг ' + (i + 1) + '…'} className="tech-step__text"
                  onChange={(e) => onChange(s.id, e.target.value)} />
                <div className="tech-step__act">
                  <IconButton icon="chevron-up" size="sm" variant="ghost" tooltip="Выше" disabled={i === 0} onClick={() => onMove(s.id, -1)} />
                  <IconButton icon="chevron-down" size="sm" variant="ghost" tooltip="Ниже" disabled={i === steps.length - 1} onClick={() => onMove(s.id, 1)} />
                  <IconButton icon="trash" size="sm" variant="ghost" tooltip="Удалить" onClick={() => onDelete(s.id)} />
                </div>
              </li>
            ))}
          </ol>
        )}
        <button type="button" className="ttk-add-row" onClick={onAdd}>
          <Icon name="plus" size={16} />
          <span>Добавить шаг</span>
        </button>
      </div>
    );
  }

export { TTKEditor };
